import React, { useState } from 'react';
import { X, Plus, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { toast } from 'react-hot-toast';

interface ImageUrlsInputProps {
  value: string[];
  onChange: (urls: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: string;
}

const ImageUrlsInput: React.FC<ImageUrlsInputProps> = ({
  value = [],
  onChange,
  placeholder = "Enter image URL...",
  className,
  disabled = false,
  error,
}) => {
  const [newUrl, setNewUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Validate URL format
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Handle adding new URL
  const handleAddUrl = () => {
    const trimmedUrl = newUrl.trim();

    if (!trimmedUrl) {
      toast.error('Please enter a URL');
      return;
    }

    if (!isValidUrl(trimmedUrl)) {
      toast.error('Please enter a valid URL');
      return;
    }

    if (value.includes(trimmedUrl)) {
      toast.error('This URL is already added');
      return;
    }

    onChange([...value, trimmedUrl]);
    setNewUrl('');
    setIsAdding(false);
    toast.success('Image URL added successfully');
  };

  // Handle removing URL
  const handleRemoveUrl = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddUrl();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setNewUrl('');
    }
  };

  // Handle cancel adding
  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewUrl('');
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Display existing URLs */}
      <div className="space-y-2">
        {value.map((url, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-md border"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0">
                <img
                  src={url}
                  alt={`Product image ${index + 1}`}
                  className="w-12 h-12 object-cover rounded border"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 truncate" title={url}>
                  {url}
                </p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open in new tab
                </a>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleRemoveUrl(index)}
              className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
              disabled={disabled}
              title="Remove image URL"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Add new URL */}
      {!isAdding ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsAdding(true)}
          disabled={disabled}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Image URL
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={placeholder}
              className="flex-1 text-sm bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]"
              disabled={disabled}
              autoFocus
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleAddUrl}
              disabled={disabled || !newUrl.trim()}
            >
              Add
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancelAdd}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Enter a valid image URL (e.g., https://example.com/image.jpg)
          </p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Help text */}
      {value.length === 0 && !isAdding && (
        <p className="text-sm text-gray-500 text-center py-2">
          No image URLs added yet. Click "Add Image URL" to get started.
        </p>
      )}
    </div>
  );
};

export default ImageUrlsInput;
